'use client';

import { usePathname } from 'next/navigation';
import React, { createContext, RefObject, useContext, useEffect, useRef, useState } from 'react';

import { AnalystProvider } from '@/components/analyst/analyst-provider';
import { CurrentOrganizationSelector } from '@/components/global/current-organization-selector';
import { useGlobalState } from '@/components/global/global-state';
import MainFooter from '@/components/global/main-footer';
import { ResearchSidebar } from '@/components/global/research-sidebar';
import SignupLoginButtonModal from '@/components/global/signup-login-button-modal';
import { StocktwitsAcquisitionAnnouncementBanner } from '@/components/global/stocktwits-acquisition-announcement-banner';
import UserProfileDropdown from '@/components/global/user-profile-dropdown';
import { IndexBuilderContextProvider } from '@/components/index-builder/useIndexBuilderContext';
import { ModalProvider } from '@/components/providers/Modal';
import { ResearchProvider } from '@/components/providers/Research';
import { usePosthogTracking } from '@/helpers/hooks/usePosthogTracking';
import { useScrollListener } from '@/helpers/hooks/useScrollListener';
import cn from '@/lib/cn';
import { LOGIN_PROTECTED_PATHNAMES } from '@/lib/constants/general';

import { GlobalNavigationBar } from '../global/global-navigation-bar';

interface MainContentContextValue {
    intersectionRef: RefObject<HTMLElement>;
    updateIntersectionRef: (ref: RefObject<HTMLElement>) => void;
}

export const MainContentContext = createContext({} as MainContentContextValue);

export const MainContentProvider = ({ children }: { children: React.ReactNode }) => {
    const startRef = useRef<HTMLDivElement>(null);
    const [intersectionRef, setIntersectionRef] = useState<RefObject<HTMLElement>>(startRef);
    const updateIntersectionRef = (ref: RefObject<HTMLElement>) => setIntersectionRef(ref);
    const value: MainContentContextValue = { intersectionRef, updateIntersectionRef };
    return (
        <MainContentContext.Provider value={value}>
            {children}
            <div ref={startRef} />
        </MainContentContext.Provider>
    );
};

export const useMainContentContext = () => {
    const context = useContext(MainContentContext);

    if (!context) {
        throw new Error('useMainContentContext must be used within a MainContentProvider');
    }

    return context;
};

export const MainContent = ({ children }: { children: React.ReactNode }) => {
    const pathname = usePathname();
    const { locations } = usePosthogTracking();
    const { globalState } = useGlobalState();
    const { currentUser, currentUserLoading } = globalState;
    const isLoggedIn = !!currentUser && !currentUserLoading;
    const isLoginProtected =
        (!!pathname && LOGIN_PROTECTED_PATHNAMES.some((protectedPath) => pathname.startsWith(protectedPath))) || false;
    const showChildren = !isLoginProtected || isLoggedIn;
    const isNotHomepage = pathname !== '/';
    const [isVisible, setIsVisibleState] = useState(true);
    const { isScrolling, y, lastY } = useScrollListener();

    useEffect(() => {
        if (isScrolling) {
            if (lastY > y && !isVisible) {
                setIsVisibleState(true);
            } else if (lastY < y && isVisible) {
                setIsVisibleState(false);
            }
        }
    }, [isScrolling, isVisible, lastY, y]);

    return (
        <div
            className={cn('min-h-full', {
                'bg-gradient-to-b to-white': isNotHomepage,
                flex: !isNotHomepage, // TODO: Need to investigate if it negatively affects the layout for all pages
            })}
        >
            <AnalystProvider>
                <IndexBuilderContextProvider rawBuilderIndexId="create">
                    <ResearchProvider>
                        <ModalProvider>
                            <div className="flex flex-col flex-grow min-h-full">
                                {isNotHomepage && <StocktwitsAcquisitionAnnouncementBanner />}

                                <CurrentOrganizationSelector />

                                {isNotHomepage && (
                                    <div className="flex items-center w-full z-2000 sticky top-6 left-0 h-12 mb-4 mt-4">
                                        {isNotHomepage && <GlobalNavigationBar />}

                                        {currentUser && isNotHomepage ? (
                                            <UserProfileDropdown
                                                className={cn(
                                                    'hidden animate-fadeIn opacity-0 lg:inline-flex absolute top-0 right-8 z-2000',
                                                    {
                                                        '-translate-y-full transition-all lg:opacity-0': !isVisible,
                                                        'translate-y-0 lg:opacity-100 transition-all': isVisible,
                                                    }
                                                )}
                                                trackingProperites={{
                                                    section: locations.GLOBAL_NAV,
                                                }}
                                            />
                                        ) : !currentUser && isNotHomepage ? (
                                            <SignupLoginButtonModal />
                                        ) : null}
                                    </div>
                                )}

                                {showChildren && children}

                                {isNotHomepage && <MainFooter className="mt-auto" />}

                                <div id="root" />

                                {isNotHomepage && <ResearchSidebar />}
                            </div>
                        </ModalProvider>
                    </ResearchProvider>
                </IndexBuilderContextProvider>
            </AnalystProvider>
        </div>
    );
};
